import React, { useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import './ProductForm.css';

const EMPTY = {
  name: '', brand: '', category: 'Lifestyle', price: '',
  original_price: '', tag: '', colorway: '', description: '',
  sizes: '', details: '', in_stock: true,
};

const CATEGORIES = [  
  'All',
  'Men',
  'Women',
  'Kids',
  'Lifestyle',
  'Running',
  'Sandals & Slides',
  'Caps',
  'Football cleats',
  'Football shirts',
  'Clothing',
  'Bags',
];
const BRANDS =  ['Nike', 'Adidas', 'Puma', 'New Balance', 'Converse', 'LaCoste', 'Jordan', 'Diesel', 'On Cloud', 'Alexander McQueen', 'Karl Lagerfeld', 'Hugo Boss', 'TimberLand', 'Birkenstock', 'Dior', 'Hoka', 'New era', 'alo'];

const TAGS       = ['', 'NEW', 'LIMITED', 'SALE', 'HOT'];

export default function ProductForm({ existing, onSave, onCancel }) {
const [form, setForm] = useState(
  existing
    ? {
        ...existing,
        sizes:   Array.isArray(existing.sizes)   ? existing.sizes.join(', ')   : existing.sizes   || '',
        details: Array.isArray(existing.details) ? existing.details.join('\n') : existing.details || '',
      }
    : EMPTY
);
  const [images, setImages]     = useState([]);
  const [previews, setPreviews] = useState(existing?.images || []);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleImages = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
    setPreviews(files.map(f => URL.createObjectURL(f)));
  };

  const uploadImages = async () => {
    const urls = [];
    for (const file of images) {
      const ext  = file.name.split('.').pop();
      const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage
        .from('product-images')
        .upload(path, file, { upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from('product-images').getPublicUrl(path);
      urls.push(data.publicUrl);
    }
    return urls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let imageUrls = existing?.images || [];
      if (images.length > 0) imageUrls = await uploadImages();

      const payload = {
        ...form,
        price:          parseInt(form.price),
        original_price: form.original_price ? parseInt(form.original_price) : null,
        sizes:   Array.isArray(form.sizes)
                ? form.sizes
                : form.sizes ? form.sizes.split(',').map(s => s.trim()).filter(Boolean) : [],
        details: Array.isArray(form.details)
                ? form.details
                : form.details ? form.details.split('\n').map(s => s.trim()).filter(Boolean) : [],
        images:  imageUrls,
      };

      let result;
      if (existing) {
        result = await supabase.from('products').update(payload).eq('id', existing.id);
      } else {
        result = await supabase.from('products').insert([payload]);
      }

      if (result.error) throw result.error;
      onSave();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="pform">
      <div className="pform__header">
        <h2 className="pform__title">
          {existing ? 'Edit Product' : 'New Product'}
        </h2>
        <button className="pform__cancel" onClick={onCancel}>✕ Cancel</button>
      </div>

      <form className="pform__body" onSubmit={handleSubmit}>
        {error && <p className="pform__error">{error}</p>}

        {/* Two column layout */}
        <div className="pform__grid">

          {/* LEFT */}
          <div className="pform__col">
            <div className="pform__section-label">Product Info</div>

            <div className="pform__row">
              <div className="pform__field">
                <label>Product Name *</label>
                <input required value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="Air Force 1 Low" />
              </div>
              <div className="pform__field">
                <label>Brand *</label>
                <select required value={form.brand} onChange={e => set('brand', e.target.value)}>
                  <option value="">— Select Brand —</option>
                  {BRANDS.map(b => <option key={b}>{b}</option>)}
                </select>
              </div>
            </div>

            <div className="pform__row">
              <div className="pform__field">
                <label>Category *</label>
                <select value={form.category} onChange={e => set('category', e.target.value)}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="pform__field">
                <label>Tag</label>
                <select value={form.tag} onChange={e => set('tag', e.target.value)}>
                  {TAGS.map(t => <option key={t} value={t}>{t || '— None —'}</option>)}
                </select>
              </div>
            </div>

            <div className="pform__field">
              <label>Colorway</label>
              <input value={form.colorway}
                onChange={e => set('colorway', e.target.value)}
                placeholder="White / Forest Green" />
            </div>

            <div className="pform__row">
              <div className="pform__field">
                <label>Price (R) *</label>
                <input required type="number" value={form.price}
                  onChange={e => set('price', e.target.value)}
                  placeholder="1899" />
              </div>
              <div className="pform__field">
                <label>Original Price (R)</label>
                <input type="number" value={form.original_price}
                  onChange={e => set('original_price', e.target.value)}
                  placeholder="2299 (optional)" />
              </div>
            </div>

            <div className="pform__field">
              <label>Description *</label>
              <textarea required rows={4} value={form.description}
                onChange={e => set('description', e.target.value)}
                placeholder="Describe the shoe — history, feel, purpose..." />
            </div>

            <div className="pform__field">
              <label>Sizes (comma separated)</label>
              <input value={form.sizes}
                onChange={e => set('sizes', e.target.value)}
                placeholder="6, 7, 7.5, 8, 8.5, 9, 9.5, 10, 11, 12" />
            </div>

            <div className="pform__field">
              <label>Product Details (one per line)</label>
              <textarea rows={4} value={form.details}
                onChange={e => set('details', e.target.value)}
                placeholder={"Leather upper\nAir-sole unit\nRubber outsole"} />
            </div>

            <div className="pform__field pform__field--inline">
              <label>In Stock</label>
              <div
                className={`pform__toggle${form.in_stock ? ' pform__toggle--on' : ''}`}
                onClick={() => set('in_stock', !form.in_stock)}
              >
                <span className="pform__toggle-knob" />
              </div>
              <span className="pform__toggle-lbl">
                {form.in_stock ? 'Available' : 'Out of Stock'}
              </span>
            </div>
          </div>

          {/* RIGHT — images */}
          <div className="pform__col">
            <div className="pform__section-label">Product Images</div>

            <label className="pform__upload-area">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImages}
                style={{ display: 'none' }}
              />
              <div className="pform__upload-inner">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <p>Click to upload images</p>
                <span>PNG, JPG, WEBP · Multiple allowed</span>
              </div>
            </label>

            {/* Previews */}
            {previews.length > 0 && (
              <div className="pform__previews">
                {previews.map((src, i) => (
                  <div key={i} className="pform__preview">
                    <img src={src} alt={`Preview ${i+1}`} />
                    {i === 0 && <span className="pform__preview-main">MAIN</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Tips */}
            <div className="pform__tips">
              <p className="pform__tips-title">Image Tips</p>
              <ul>
                <li>First image = main display photo</li>
                <li>Use square images (1:1) for best results</li>
                <li>Minimum 800×800px recommended</li>
                <li>Max 5 images per product</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="pform__footer">
          <button type="button" className="pform__btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="pform__btn-save" disabled={loading}>
            {loading
              ? 'Saving...'
              : existing ? 'Save Changes' : 'Create Listing'}
          </button>
        </div>
      </form>
    </div>
  );
}